import { useState } from "react";
import type { CostBucket, CostByTaskResponse } from "../types";
import "./PanelCostByTask.css";

const NUM_FMT = new Intl.NumberFormat("en-GB");

function fmt(n: number): string {
  return NUM_FMT.format(n);
}

type GroupBy = "task" | "entity";

function CostRow({ row, labelKey }: { row: CostBucket; labelKey: "task_ref" | "entity_id" }) {
  const label = row[labelKey] ?? "—";
  const isUntagged = labelKey === "task_ref" && label === "untagged";
  return (
    <div className="crow" data-key={label}>
      <div className="crow-main">
        <span className={`crow-label${isUntagged ? " crow-label-untagged" : ""}`}>
          {label}
        </span>
        <span className="crow-total">{fmt(row.tokens_total)} tok</span>
      </div>
      <div className="crow-breakdown">
        <span className="crow-in">in {fmt(row.tokens_input)}</span>
        <span className="crow-out">out {fmt(row.tokens_output)}</span>
        <span className="crow-turns">{row.turn_count} turn{row.turn_count === 1 ? "" : "s"}</span>
        {row.entities && row.entities.length > 0 && (
          <span className="crow-tag" title={row.entities.join(", ")}>
            {row.entities.length} entit{row.entities.length === 1 ? "y" : "ies"}
          </span>
        )}
        {row.tasks && row.tasks.length > 0 && (
          <span className="crow-tag" title={row.tasks.join(", ")}>
            {row.tasks.length} task{row.tasks.length === 1 ? "" : "s"}
          </span>
        )}
      </div>
    </div>
  );
}

export default function PanelCostByTask({
  costByTask,
}: {
  costByTask: CostByTaskResponse | null;
}) {
  const [groupBy, setGroupBy] = useState<GroupBy>("task");

  const isEmptyBeforeFirstLoad = costByTask === null;
  const isUnavailable = costByTask !== null && costByTask.telemetry_available === false;
  const rows: CostBucket[] =
    costByTask && costByTask.telemetry_available
      ? groupBy === "task"
        ? costByTask.by_task
        : costByTask.by_entity
      : [];
  const labelKey = groupBy === "task" ? "task_ref" : "entity_id";
  const populated = rows.length > 0;

  return (
    <section
      className={`panel panel-cost-by-task ${populated ? "is-populated" : "is-empty"}`}
      aria-labelledby="panel-cost-by-task-title"
    >
      <header className="panel-head">
        <h2 className="panel-title" id="panel-cost-by-task-title">
          Token cost per task
        </h2>
        <span className="panel-meta">
          {costByTask && costByTask.telemetry_available && (
            <span className="panel-count">{fmt(costByTask.turn_count)}</span>
          )}
          <span className="panel-sub">
            {costByTask
              ? `${costByTask.source_table} · last ${costByTask.window_days}d · token counts only, no dollar estimate`
              : "trumin-session-telemetry"}
          </span>
        </span>
      </header>
      <div className="cbt-toolbar" role="tablist" aria-label="Group cost by">
        <button
          type="button"
          role="tab"
          aria-selected={groupBy === "task"}
          className={`cbt-tab${groupBy === "task" ? " cbt-tab-active" : ""}`}
          onClick={() => setGroupBy("task")}
        >
          By task
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={groupBy === "entity"}
          className={`cbt-tab${groupBy === "entity" ? " cbt-tab-active" : ""}`}
          onClick={() => setGroupBy("entity")}
        >
          By entity
        </button>
        {costByTask && costByTask.telemetry_available && (
          <span className="cbt-coverage">
            task_ref coverage: {costByTask.task_ref_coverage_pct}%
            {costByTask.task_ref_coverage_pct === 0 && (
              <span className="cbt-coverage-note">
                {" "}
                (writer v0.2.0 per-task tagging not yet rolled out — all turns bucket
                under "untagged")
              </span>
            )}
          </span>
        )}
      </div>
      <div className="panel-body">
        {isEmptyBeforeFirstLoad && (
          <div className="panel-empty">Loading token cost…</div>
        )}
        {isUnavailable && (
          <div className="panel-empty cbt-unavailable">
            Telemetry not readable yet
            {costByTask?.error === "dynamodb_read_failed" && (
              <>
                {" "}
                — dashboard Lambda lacks a DynamoDB read grant on{" "}
                {costByTask.source_table} (IAM grant pending, routed via
                boardroom).
              </>
            )}
          </div>
        )}
        {costByTask && costByTask.telemetry_available && !populated && (
          <div className="panel-empty">No telemetry in the selected window.</div>
        )}
        {rows.map((row) => (
          <CostRow key={String(row[labelKey])} row={row} labelKey={labelKey} />
        ))}
      </div>
    </section>
  );
}
